import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@clerk/react";
import { Send, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import Shell from "@/components/Shell";
import { makeApi } from "@/lib/api";

const when = (iso) => new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

function Thread({ api, thread, onBack, onSent }) {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const load = useCallback(() => api.get(`/admin/messages/${thread.user_id}`).then(({ data }) => setMsgs(data.messages)), [api, thread.user_id]);
  useEffect(() => { load(); }, [load]);
  const reply = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      await api.post(`/admin/messages/${thread.user_id}`, { body: text });
      setText("");
      load();
      onSent();
    } catch {
      toast.error("Could not send reply.");
    }
  };
  return (
    <div className="brutal-border flex flex-col h-[70dvh]" data-testid="admin-thread">
      <div className="px-4 py-3 border-b border-[#262626] bg-[#141414] flex items-center gap-2">
        <button onClick={onBack} className="md:hidden min-h-[44px] pr-2" aria-label="Back" data-testid="admin-thread-back"><ChevronLeft size={18} /></button>
        <div className="min-w-0">
          <div className="font-heading font-bold truncate">{thread.legal_name || thread.name || "Client"}</div>
          <div className="text-xs text-muted-foreground truncate">{thread.email}</div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {msgs.map((m, i) => (
          <div key={i} className={`flex ${m.sender === "admin" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[80%] px-3 py-2 text-sm whitespace-pre-wrap break-words ${m.sender === "admin" ? "bg-[#0055FF] text-white" : "bg-[#141414] border border-[#262626]"}`}>
              {m.body}
              <div className="text-[10px] opacity-60 mt-1">{when(m.created_at)}</div>
            </div>
          </div>
        ))}
      </div>
      <form onSubmit={reply} className="border-t border-[#262626] p-2 flex gap-2">
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Write a reply…" data-testid="admin-reply-input"
          className="flex-1 min-w-0 min-h-[44px] bg-[#141414] border border-[#262626] px-3 text-base sm:text-sm focus:outline-none focus:border-white" />
        <button type="submit" data-testid="admin-reply-send" className="cta-primary shrink-0 min-h-[44px] px-4 inline-flex items-center gap-2 font-mono-label text-xs"><Send size={14} /> Reply</button>
      </form>
    </div>
  );
}

export default function AdminMessages() {
  const { getToken } = useAuth();
  const api = useMemo(() => makeApi(getToken), [getToken]);
  const [threads, setThreads] = useState(null);
  const [active, setActive] = useState(null);
  const load = useCallback(() => api.get("/admin/messages").then(({ data }) => setThreads(data.threads)), [api]);
  useEffect(() => { load(); }, [load]);

  return (
    <Shell>
      <div className="mb-6">
        <div className="font-mono-label text-xs text-[#FFD600] mb-2">OWNER · MESSAGES</div>
        <h1 className="font-heading font-black text-3xl sm:text-4xl leading-none" data-testid="admin-messages-title">Client messages</h1>
      </div>
      {!threads ? <div className="font-mono-label text-xs text-muted-foreground">LOADING…</div> : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 [&>*]:min-w-0">
          <div className={`brutal-border divide-y divide-[#262626] h-fit ${active ? "hidden md:block" : ""}`} data-testid="admin-thread-list">
            {threads.length === 0 && <div className="p-4 text-sm text-muted-foreground">No messages yet.</div>}
            {threads.map((t) => (
              <button key={t.user_id} onClick={() => setActive(t)} data-testid={`admin-thread-${t.user_id}`}
                className={`w-full text-left p-4 min-h-[64px] hover:bg-[#141414] ${active?.user_id === t.user_id ? "bg-[#141414]" : ""}`}>
                <div className="flex justify-between gap-2">
                  <span className="font-semibold text-sm truncate">{t.legal_name || t.name || t.email || "Client"}</span>
                  {t.unread > 0 && <span className="bg-[#FFD600] text-[#0A0A0A] text-[10px] font-bold px-2 py-0.5 shrink-0">{t.unread} new</span>}
                </div>
                <div className="text-xs text-muted-foreground truncate mt-1">{t.last}</div>
              </button>
            ))}
          </div>
          <div className={`md:col-span-2 ${active ? "" : "hidden md:block"}`}>
            {active ? <Thread key={active.user_id} api={api} thread={active} onBack={() => setActive(null)} onSent={load} />
              : <div className="brutal-border p-6 text-sm text-muted-foreground">Pick a conversation to read and reply.</div>}
          </div>
        </div>
      )}
    </Shell>
  );
}
